/**
 * The full GSTR-2B, as the GST portal's Excel shows it.
 * @file src/web/features/admin/components/Gstr2bWorkbookView.tsx
 *
 * The same tabs as the portal's download — the four summaries, then B2B, B2BA,
 * B2B-CDNR … — with the same column names and order, the amendment tabs'
 * "Original Details | Revised Details" header, a count on each tab (an empty
 * one in a lighter style, still openable), sorting, search and a totals row. On B2B and B2B-CDNR only, two
 * columns of ours at the end: "In our books" and "Decision", with the same
 * Record / Hold back / Claim anyway controls and bulk selection as the
 * reconciliation.
 *
 * Download Excel gives the portal's workbook back, ours added at the end.
 */
import { useEffect, useMemo, useState } from 'react';
import { ArrowDown, ArrowUp, Download, Loader2, Search } from 'lucide-react';
import { adminFetch, adminFetchJson } from '../lib/admin-api';
import { BulkDecisionBar, BulkOutcome, DecisionControls, inr, keyOf, sameBill, type BillLine, type BulkResult } from './NotInBooksList';

type Cell = string | number | boolean | null;
interface Column { leaf: string; group: string | null; part: string | null }
interface Ours { inBooks: string; decision: string; line: BillLine | null }
interface DetailTab { name: string; title: string; columns: Column[]; rows: Cell[][]; ours: Ours[] | null }
interface Workbook {
  ok: boolean; loaded: boolean; source?: 'portal-xlsx' | 'gsp-json';
  meta?: { financialYear: string | null; taxPeriod: string | null; gstin: string | null; legalName: string | null; generatedOn: string | null };
  summaries?: { name: string; rows: Cell[][] }[];
  tabs?: DetailTab[];
}

export const WB_PAGE = 50;
const fmt = (v: Cell) => (typeof v === 'number' ? inr(v) : v == null ? '' : String(v));

/** Runs of equal values, for colSpan in a header row. */
function runs<T>(xs: T[], same: (a: T, b: T) => boolean) {
  const out: { v: T; from: number; span: number }[] = [];
  xs.forEach((x, i) => {
    const last = out[out.length - 1];
    if (last && same(last.v, x)) last.span++;
    else out.push({ v: x, from: i, span: 1 });
  });
  return out;
}

export function Gstr2bWorkbookView({ period, entityId, onChanged, onRecord }: {
  period: string; entityId: string | null;
  /** After a decision: the reconciliation (and 3B) re-read. */
  onChanged: () => void;
  /** Open the single-bill record dialog. */
  onRecord?: (line: BillLine) => void;
}) {
  const [wb, setWb] = useState<Workbook | null>(null);
  const [err, setErr] = useState<string | null>(null);
  const [tab, setTab] = useState<string>('B2B');
  const [reload, setReload] = useState(0);
  const [busy, setBusy] = useState<null | 'download'>(null);
  const [showEmpty, setShowEmpty] = useState(false);

  useEffect(() => {
    let gone = false;
    setErr(null);
    adminFetchJson<Workbook>('/api/gst/2b/workbook', { query: { period, entityId: entityId ?? undefined } })
      .then((w) => { if (!gone) setWb(w); })
      .catch((e) => { if (!gone) setErr(e instanceof Error ? e.message.replace(/^\d+\s/, '') : 'Could not load the 2B'); });
    return () => { gone = true; };
  }, [period, entityId, reload]);

  const changed = () => { setReload((n) => n + 1); onChanged(); };

  const download = async () => {
    setBusy('download'); setErr(null);
    try {
      const res = await adminFetch('/api/gst/2b/workbook.xlsx', { query: { period, entityId: entityId ?? undefined } });
      if (!res.ok) { const b = await res.json().catch(() => ({})); throw new Error(b.error || b.message || `The server answered ${res.status}`); }
      const name = /filename="([^"]+)"/.exec(res.headers.get('Content-Disposition') ?? '')?.[1] ?? `${period}_GSTR2B.xlsx`;
      const url = URL.createObjectURL(await res.blob());
      const a = document.createElement('a'); a.href = url; a.download = name; a.click();
      setTimeout(() => URL.revokeObjectURL(url), 5000);
    } catch (e) { setErr(e instanceof Error ? e.message : 'Download failed'); } finally { setBusy(null); }
  };

  const toolbar = (
    <div className="flex flex-wrap items-center gap-2">
      {wb?.loaded && (
        <button onClick={download} disabled={!!busy}
          className="inline-flex items-center gap-1.5 rounded-full bg-inkblack px-3 py-1.5 text-xs font-semibold text-white disabled:opacity-50">
          {busy === 'download' ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Download className="h-3.5 w-3.5" />} Download Excel
        </button>
      )}
    </div>
  );

  if (!wb && !err) return <p className="flex items-center gap-2 text-sm text-ink-500"><Loader2 className="h-4 w-4 animate-spin" /> Loading the GSTR-2B…</p>;
  if (wb && !wb.loaded) {
    return (
      <div className="space-y-3 rounded-2xl border border-dashed border-ink-300 px-5 py-8 text-center">
        <p className="text-sm text-ink-600">No GSTR-2B is loaded for this period. Fetch it from the portal.</p>
        <div className="flex justify-center">{toolbar}</div>
        {err && <p role="alert" className="text-sm text-danger-700">{err}</p>}
      </div>
    );
  }

  const tabs = wb?.tabs ?? [];
  const summaries = wb?.summaries ?? [];
  const current = tabs.find((t) => t.name === tab) ?? null;
  /* What is worth showing: the tables that have records, plus whichever one
     is open — an empty table reached from "+ N empty" must not vanish under
     the person reading it. When the whole 2B is empty, show them all rather
     than an empty row of nothing. */
  const withRows = tabs.filter((t) => t.rows.length);
  const shownTabs = showEmpty || !withRows.length
    ? tabs
    : tabs.filter((t) => t.rows.length || t.name === tab);
  const hiddenCount = tabs.length - shownTabs.length;
  const currentSummary = summaries.find((s) => s.name === tab) ?? null;

  return (
    <div className="space-y-3">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <p className="text-xs text-ink-500">
          {wb?.meta?.gstin} · {wb?.meta?.taxPeriod} {wb?.meta?.financialYear}
          {wb?.meta?.generatedOn ? ` · generated ${wb.meta.generatedOn}` : ''}
          {' · '}{wb?.source === 'portal-xlsx' ? 'from the portal’s Excel' : 'from the 2B fetched from the portal'}
        </p>
        {toolbar}
      </div>
      {err && <p role="alert" className="rounded-xl border border-danger-200 bg-danger-50 px-4 py-2.5 text-sm text-danger-700">{err}</p>}

      {/* Two rows, not one wall.
          A 2B has four summaries and twenty-five detail tables, and in a
          normal month all but one or two of the tables are empty. Showing
          twenty-nine chips of which twenty-four read "0" buries the one that
          matters. The summaries get their own segmented control; the tables
          show what has records, and the empties fold away behind a count —
          still openable, because the portal's headers are worth seeing. */}
      <div className="space-y-2 border-b border-ink-200 pb-3">
        {!!summaries.length && (
          <div role="tablist" aria-label="GSTR-2B summaries" className="flex flex-wrap items-center gap-2">
            <span className="text-[11px] font-semibold uppercase tracking-wide text-ink-400">Summary</span>
            <div className="flex flex-wrap gap-0.5 rounded-full border border-ink-200 p-0.5">
              {summaries.map((s) => (
                <button key={s.name} role="tab" aria-selected={tab === s.name} onClick={() => setTab(s.name)}
                  className={`rounded-full px-3 py-1 text-xs font-semibold transition ${tab === s.name ? 'bg-inkblack text-paper' : 'text-ink-600 hover:text-ink-900'}`}>
                  {s.name}
                </button>
              ))}
            </div>
          </div>
        )}

        <div role="tablist" aria-label="GSTR-2B tables" className="flex flex-wrap items-center gap-1.5">
          <span className="mr-0.5 text-[11px] font-semibold uppercase tracking-wide text-ink-400">Tables</span>
          {shownTabs.map((t) => {
            const empty = !t.rows.length;
            return (
              <button key={t.name} role="tab" aria-selected={tab === t.name} onClick={() => setTab(t.name)}
                title={empty ? 'No records for this period' : `${t.rows.length} record${t.rows.length === 1 ? '' : 's'}`}
                className={`inline-flex items-center gap-1.5 rounded-full border px-3 py-1 text-xs transition ${
                  tab === t.name
                    ? 'border-inkblack bg-inkblack font-semibold text-paper'
                    : empty
                      ? 'border-ink-200 font-normal text-ink-400 hover:bg-ink-50'
                      : 'border-ink-300 font-semibold text-ink-800 hover:bg-ink-50'}`}>
                {t.name}
                {!empty && (
                  <span className={`rounded-full px-1.5 text-[10px] tabular-nums ${tab === t.name ? 'bg-white/20' : 'bg-ink-100 text-ink-600'}`}>
                    {t.rows.length}
                  </span>
                )}
              </button>
            );
          })}
          {!!hiddenCount && (
            <button onClick={() => setShowEmpty((v) => !v)}
              className="rounded-full px-2.5 py-1 text-xs font-semibold text-ink-500 underline underline-offset-2 hover:text-ink-800">
              {showEmpty ? 'Hide the empty ones' : `+ ${hiddenCount} empty`}
            </button>
          )}
        </div>
      </div>

      {currentSummary && <SummaryTable rows={currentSummary.rows} />}
      {current && <DetailTable key={current.name} tab={current} period={period} entityId={entityId} onChanged={changed} onRecord={onRecord} />}
    </div>
  );
}

/** A summary tab, from its header row down. */
function SummaryTable({ rows }: { rows: Cell[][] }) {
  const header = rows[5] ?? [];
  const body = rows.slice(6);
  return (
    <div className="overflow-x-auto rounded-2xl border border-ink-200 bg-white">
      <p className="border-b border-ink-100 px-4 py-2 text-sm font-semibold text-ink-900">{String(rows[4]?.[0] ?? '')}</p>
      <table className="w-full min-w-[48rem] text-sm">
        <thead>
          <tr className="border-b border-ink-100 text-left text-xs text-ink-500">
            {header.slice(0, 8).map((h, i) => <th key={i} className={`px-3 py-2 font-medium ${i >= 3 && i <= 6 ? 'text-right' : ''}`}>{fmt(h)}</th>)}
          </tr>
        </thead>
        <tbody className="divide-y divide-ink-50">
          {body.map((r, i) => {
            const section = r[0] != null && r.slice(2).every((v) => v == null);
            return (
              <tr key={i} className={section ? 'bg-ink-50 font-semibold text-ink-800' : /^[IVX]+$/.test(String(r[0] ?? '')) ? 'font-semibold' : ''}>
                {section
                  ? <td colSpan={8} className="px-3 py-2">{fmt(r[0])}{r[1] ? ` — ${fmt(r[1])}` : ''}</td>
                  : r.slice(0, 8).map((v, j) => (
                    <td key={j} className={`whitespace-pre-line px-3 py-1.5 ${j >= 3 && j <= 6 ? 'text-right tabular-nums' : ''} ${j === 7 ? 'max-w-md text-xs text-ink-500' : ''}`}>{fmt(v)}</td>
                  ))}
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

/** A detail tab: the portal's columns, sortable, searchable, totalled, paged; ours at the end of B2B and B2B-CDNR. */
function DetailTable({ tab, period, entityId, onChanged, onRecord }: { tab: DetailTab; period: string; entityId: string | null; onChanged: () => void; onRecord?: (line: BillLine) => void }) {
  const [q, setQ] = useState('');
  const [sort, setSort] = useState<{ col: number; dir: 1 | -1 } | null>(null);
  const [page, setPage] = useState(0);
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [result, setResult] = useState<BulkResult | null>(null);
  const [error, setError] = useState<string | null>(null);
  const cols = tab.columns;
  const withOurs = !!tab.ours;

  const indexed = useMemo(() => tab.rows.map((r, i) => ({ r, ours: tab.ours?.[i] ?? null })), [tab]);
  const filtered = useMemo(() => {
    const t = q.trim().toLowerCase();
    let xs = t ? indexed.filter((x) => x.r.some((v) => String(v ?? '').toLowerCase().includes(t))) : indexed;
    if (sort) {
      xs = [...xs].sort((a, b) => {
        const x = a.r[sort.col], y = b.r[sort.col];
        if (typeof x === 'number' && typeof y === 'number') return (x - y) * sort.dir;
        return String(x ?? '').localeCompare(String(y ?? ''), 'en-IN', { numeric: true }) * sort.dir;
      });
    }
    return xs;
  }, [indexed, q, sort]);
  const pages = Math.max(1, Math.ceil(filtered.length / WB_PAGE));
  const at = Math.min(page, pages - 1);
  const shown = filtered.slice(at * WB_PAGE, at * WB_PAGE + WB_PAGE);

  // Totals over the filtered rows, for every column that holds amounts.
  const numeric = cols.map((_, c) => tab.rows.some((r) => typeof r[c] === 'number'));
  const totals = cols.map((_, c) => (numeric[c] ? filtered.reduce((t, x) => t + (typeof x.r[c] === 'number' ? (x.r[c] as number) : 0), 0) : null));

  const choosable = filtered.filter((x) => x.ours?.line).map((x) => x.ours!.line!);
  const picked = choosable.filter((l) => selected.has(keyOf(l)));
  const pickedTax = picked.reduce((t, l) => t + Math.abs(l.portal?.tax ?? 0), 0);
  const allOn = choosable.length > 0 && choosable.every((l) => selected.has(keyOf(l)));
  const toggle = (l: BillLine) => setSelected((s) => { const n = new Set(s); const k = keyOf(l); if (n.has(k)) n.delete(k); else n.add(k); return n; });
  const toggleAll = () => setSelected((s) => { const n = new Set(s); for (const l of choosable) { if (allOn) n.delete(keyOf(l)); else n.add(keyOf(l)); } return n; });
  const done = (r: BulkResult | { error: string }) => {
    if ('error' in r) { setError(r.error); return; }
    setError(null); setResult(r);
    setSelected(new Set(picked.filter((l) => r.failed.some((f) => sameBill(f, l))).map(keyOf)));
    onChanged();
  };

  const hasPart = cols.some((c) => c.part);
  const levels = hasPart ? 3 : 2;
  const sortBy = (c: number) => setSort((s) => (s?.col === c ? { col: c, dir: s.dir === 1 ? -1 : 1 } : { col: c, dir: 1 }));
  const arrow = (c: number) => (sort?.col === c ? (sort.dir === 1 ? <ArrowUp className="inline h-3 w-3" /> : <ArrowDown className="inline h-3 w-3" />) : null);
  const leafCell = (c: number, rowSpan: number) => (
    <th key={`l${c}`} rowSpan={rowSpan} className="cursor-pointer whitespace-nowrap border-b border-l border-ink-100 px-2.5 py-1.5 font-medium hover:text-ink-900" onClick={() => sortBy(c)} aria-sort={sort?.col === c ? (sort.dir === 1 ? 'ascending' : 'descending') : undefined}>
      {cols[c].leaf} {arrow(c)}
    </th>
  );

  return (
    <div className="space-y-2">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <p className="text-sm font-semibold text-ink-900">{tab.title}</p>
        <div className="relative w-full max-w-xs">
          <Search className="absolute left-2.5 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-ink-400" />
          <input value={q} onChange={(e) => { setQ(e.target.value); setPage(0); }} aria-label={`Search ${tab.name}`} placeholder="Search this tab"
            className="w-full rounded-lg border border-ink-200 bg-white py-1.5 pl-8 pr-2 text-xs focus:outline-none" />
        </div>
      </div>
      <BulkOutcome result={result} error={error} />
      <div className="overflow-x-auto rounded-2xl border border-ink-200 bg-white">
        <table className="w-full text-xs">
          <thead className="bg-ink-50 text-left text-ink-600">
            {hasPart && (
              <tr>
                {runs(cols.map((c) => c.part), (a, b) => a === b).map((g) => (
                  <th key={`p${g.from}`} colSpan={g.span} className="border-b border-l border-ink-100 px-2.5 py-1.5 text-center font-semibold">{g.v ?? ''}</th>
                ))}
                {withOurs && <th rowSpan={1} className="sticky right-0 z-20 border-b border-l border-ink-200 bg-highlighter-40 px-2.5" />}
              </tr>
            )}
            <tr>
              {runs(cols.map((c, i) => ({ c, i })), (a, b) => !!a.c.group && a.c.group === b.c.group && a.c.part === b.c.part).map((g) =>
                g.v.c.group
                  ? <th key={`g${g.from}`} colSpan={g.span} className="border-b border-l border-ink-100 px-2.5 py-1.5 text-center font-semibold">{g.v.c.group}</th>
                  : leafCell(g.from, levels - (hasPart ? 1 : 0)))}
              {withOurs && (
                <>
                  {/* One pinned column, not two.
                      The portal's own tables run to twenty-odd columns, so ours
                      sat off the end of a horizontal scroll: the Decision
                      control — the only thing on this screen a CA acts on —
                      was invisible until you scrolled to find it. "In our
                      books" had its own column saying "Not in books" beside
                      three buttons that exist only for bills that are not in
                      the books; where a bill IS matched, the voucher it
                      matched is worth saying, and that is said here instead. */}
                  <th rowSpan={levels - (hasPart ? 1 : 0)} className="sticky right-0 z-20 w-[16rem] min-w-[16rem] whitespace-nowrap border-b border-l border-ink-300 bg-highlighter-40 px-2.5 py-1.5 font-semibold text-ink-800 shadow-[-8px_0_12px_-8px_rgba(0,0,0,0.25)]">
                    <label className="inline-flex items-center gap-1.5">
                      <input type="checkbox" aria-label="Select all" checked={allOn} onChange={toggleAll} disabled={!choosable.length} /> Decision
                    </label>
                  </th>
                </>
              )}
            </tr>
            <tr>{cols.map((c, i) => (c.group ? leafCell(i, 1) : null))}</tr>
          </thead>
          <tbody className="divide-y divide-ink-50">
            {shown.map((x, i) => (
              <tr key={i} className="group/row align-top hover:bg-ink-50/60">
                {cols.map((_, c) => <td key={c} className={`whitespace-nowrap px-2.5 py-1.5 ${numeric[c] ? 'text-right tabular-nums' : ''}`}>{fmt(x.r[c])}</td>)}
                {withOurs && (
                  <>
                    <td className="sticky right-0 z-10 w-[16rem] min-w-[16rem] border-l border-ink-300 bg-white px-2.5 py-1.5 shadow-[-8px_0_12px_-8px_rgba(0,0,0,0.25)]">
                      {x.ours?.line ? (
                        <div className="flex items-center gap-1.5">
                          <input type="checkbox" className="shrink-0" aria-label={`Select ${x.ours.line.invoiceNumber ?? ''}`} checked={selected.has(keyOf(x.ours.line))} onChange={() => toggle(x.ours!.line!)} />
                          <DecisionControls line={x.ours.line} period={period} entityId={entityId} onChanged={onChanged} onRecord={() => onRecord?.(x.ours!.line!)} compact />
                        </div>
                      ) : (
                        /* Matched, or otherwise nothing to decide: the voucher
                           it matched is the useful thing to show. */
                        <span className="text-[11px] text-ink-500">{x.ours?.inBooks || x.ours?.decision || ''}</span>
                      )}
                    </td>
                  </>
                )}
              </tr>
            ))}
            {!shown.length && (
              <tr><td colSpan={cols.length + (withOurs ? 2 : 0)} className="px-4 py-5 text-center text-ink-500">
                {tab.rows.length ? 'Nothing matches the search.' : 'No records for this period'}
              </td></tr>
            )}
          </tbody>
          <tfoot>
            <tr className="border-t border-ink-200 bg-ink-50 font-semibold">
              {cols.map((_, c) => <td key={c} className="whitespace-nowrap px-2.5 py-1.5 text-right tabular-nums">{c === 0 ? <span className="float-left">Total ({filtered.length})</span> : totals[c] != null ? inr(totals[c]!) : ''}</td>)}
              {withOurs && <><td /><td /></>}
            </tr>
          </tfoot>
        </table>
      </div>
      {pages > 1 && (
        <div className="flex items-center justify-end gap-2 text-xs text-ink-600">
          <button onClick={() => setPage(Math.max(0, at - 1))} disabled={at === 0} className="rounded-lg border border-ink-200 px-2.5 py-1 disabled:opacity-40">Previous</button>
          <span>Page {at + 1} of {pages}</span>
          <button onClick={() => setPage(Math.min(pages - 1, at + 1))} disabled={at >= pages - 1} className="rounded-lg border border-ink-200 px-2.5 py-1 disabled:opacity-40">Next</button>
        </div>
      )}
      {withOurs && <BulkDecisionBar picked={picked} pickedTax={pickedTax} period={period} entityId={entityId} onDone={done} onClear={() => setSelected(new Set())} />}
    </div>
  );
}
